import BaseAPI from "./BaseAPI";
import { productionRunRoute } from "../routes";

/**
 * Atomic multi-recipe production commit. The operator explicitly allocates the
 * batch(es) drawn from for each ingredient (batch_allocations); the server
 * validates those allocations cover demand with in-date stock, decrements them,
 * and creates the resulting Product documents in a single Mongo transaction.
 *
 * Requires Idempotency-Key on every request — replays return the cached run.
 */
export default class ProductionRunAPI extends BaseAPI {
    constructor(emitterInstance) {
        super(emitterInstance, productionRunRoute, "production-run");
    }

    /**
     * @param {Object} payload
     * @param {string} payload.location_id
     * @param {number} payload.started_at  ms epoch
     * @param {boolean} [payload.skip_stock_deduction]  when true, products are
     *   created and the run is audited but intake balances are not changed
     * @param {Array<{
     *   recipe_id: string,
     *   quantity: number,
     *   expiration_date?: number,
     *   unit_weights?: number[],
     *   batch_allocations?: Object<string, Array<{intake_id: string, amount: number}>>,
     *   assigned_to?: string
     * }>} payload.lines
     * @param {string} idempotencyKey  uuid; same key returns the cached response
     * @returns {Promise<{run_id: string, committed_at: number, stock_deducted: boolean, products: Array, intakes_consumed: Array}>}
     */
    async Create(payload, idempotencyKey) {
        const result = await this._request("post", this._url(), payload, {
            headers: { "Idempotency-Key": idempotencyKey }
        });
        this._emit("add", result?.run_id);
        return result;
    }

    /**
     * Fetch recent production runs, most recent first.
     *
     * @param {Object} [params]
     * @param {string} [params.location_id]
     * @param {number} [params.from]  ms epoch, inclusive
     * @param {number} [params.to]    ms epoch, exclusive
     * @param {number} [params.limit] default 50, max 200
     * @returns {Promise<Array>}
     */
    async List({ location_id, intake_id, from, to, limit } = {}, options = {}) {
        const qs = new URLSearchParams();
        if (location_id) qs.set("location_id", location_id);
        if (intake_id) qs.set("intake_id", intake_id);
        if (from != null) qs.set("from", String(from));
        if (to != null) qs.set("to", String(to));
        if (limit != null) qs.set("limit", String(limit));
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this._request("get", this._url(suffix), null, options);
    }

    /**
     * Fetch one production run by id.
     *
     * @param {string} id
     * @returns {Promise<Object>}
     */
    async GetByID(id) {
        return this._request("get", this._url(`/${id}`));
    }
}
